/**
 * RegisterSelectPage Component
 * VROONG 회원가입 사용자 유형 선택 페이지 (본사/협력사)
 *
 * @description
 * 회원가입 전 사용자 유형을 선택하는 화면
 * 본사 관리자는 직원 확인 코드, 협력사는 협력사 ID 확인 필요
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '../../../api/authApi';
import { extractErrorMessage } from '@/shared/utils/errorHandler';
import BrandSection from '../../ui/BrandSection/BrandSection';
import VersionInfo from '../../ui/VersionInfo';
import RegisterCard from '../../ui/RegisterCard';
import { LoginIcon } from '@/shared/components/icons/Icons';
import { LOGIN_FEATURES } from '@/features/auth/config/feature.config';
import '@/features/auth/styles/auth.css';
import { SupplierInfo } from '../../../types/auth.types';

// ============================================================
// 상수 정의
// ============================================================

/** 카드 컨테이너 스타일 */
const CARD_CONTAINER_STYLE: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    marginTop: '2rem',
};

/** 추가 옵션 컨테이너 스타일 */
const ADDITIONAL_OPTIONS_STYLE: React.CSSProperties = {
    marginTop: '2rem',
};

/** 로그인 링크 버튼 스타일 */
const LOGIN_LINK_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#666666',
    background: 'none',
    border: 'none',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'color 0.3s ease',
    marginBottom: '50px',
};

/** 본사 성공 메시지 스타일 */
const HEADQUARTERS_SUCCESS_STYLE: React.CSSProperties = {
    background: '#ECFDF5',
    color: '#059669',
    border: '1px solid #A7F3D0',
};

/** 협력사 성공 메시지 스타일 */
const SUPPLIER_SUCCESS_STYLE: React.CSSProperties = {
    background: '#EFF6FF',
    color: '#0091FF',
    border: '1px solid #BFDBFE',
};

/** 본사 카드 색상 설정 */
const HEADQUARTERS_COLORS = {
    gradient: { from: '#05CD82', to: '#04B374' },
    border: '#05CD82',
    background: '#F0FDF9',
    button: '#05CD82',
} as const;

/** 협력사 카드 색상 설정 */
const SUPPLIER_COLORS = {
    gradient: { from: '#0091FF', to: '#007CE6' },
    border: '#0091FF',
    background: '#F0F9FF',
    button: '#0091FF',
} as const;

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 회원가입 유형 선택 페이지
 */
const RegisterSelectPage: React.FC = () => {
    // ======== Hooks ========
    const navigate = useNavigate();
    const { checkSupplierId, isLoading } = useAuth();

    // ======== State - 협력사 ========
    const [supplierId, setSupplierId] = useState('');
    const [supplierIdChecked, setSupplierIdChecked] = useState(false);
    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo | null>(null);
    const [supplierError, setSupplierError] = useState('');

    // ======== State - 본사 ========
    const [headquartersCode, setHeadquartersCode] = useState('');
    const [headquartersCodeChecked, setHeadquartersCodeChecked] = useState(false);
    const [headquartersError, setHeadquartersError] = useState('');
    const [isCheckingCode, setIsCheckingCode] = useState(false);

    // ======== Memoized Values ========
    /** 브랜드 섹션 설명 텍스트 */
    const brandDescription = useMemo(() => (
        <>
            효율적이고 투명한 라이더 정산 관리
            <br />
            VROONG과 함께 더 나은 배달 서비스를 만들어가세요
        </>
    ), []);

    /** 본사 확인 완료 메시지 */
    const headquartersSuccessMessage = useMemo(() => {
        if (!headquartersCodeChecked) return null;
        return (
            <div style={HEADQUARTERS_SUCCESS_STYLE}>
                ✓ 본사 직원 확인이 완료되었습니다
            </div>
        );
    }, [headquartersCodeChecked]);

    /** 협력사 확인 완료 메시지 */
    const supplierSuccessMessage = useMemo(() => {
        if (!supplierInfo) return null;
        return (
            <div style={SUPPLIER_SUCCESS_STYLE}>
                ✓ <strong>{supplierInfo.suppName}</strong> ({supplierInfo.suppCompName})
            </div>
        );
    }, [supplierInfo]);

    // ======== Event Handlers ========
    /**
     * 본사 직원 확인 코드 검증
     */
    const handleCheckHeadquartersCode = useCallback(async () => {
        if (!headquartersCode) {
            setHeadquartersError('본사 직원 확인 코드를 입력해주세요.');
            return;
        }

        setIsCheckingCode(true);
        setHeadquartersError('');

        try {
            const response = await authApi.verifyHeadquartersCode({ code: headquartersCode });

            if (response.valid) {
                setHeadquartersCodeChecked(true);
                setHeadquartersError('');
            } else {
                setHeadquartersError(response.message || '올바르지 않은 확인 코드입니다.');
                setHeadquartersCodeChecked(false);
            }
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err, '코드 확인에 실패했습니다.');
            setHeadquartersError(errorMessage);
            setHeadquartersCodeChecked(false);
        } finally {
            setIsCheckingCode(false);
        }
    }, [headquartersCode]);

    /**
     * 협력사 ID 확인
     */
    const handleCheckSupplierId = useCallback(async () => {
        if (!supplierId) {
            setSupplierError('협력사 ID를 입력해주세요.');
            return;
        }

        try {
            const response = await checkSupplierId(supplierId);

            if (response.exists && response.supplierInfo) {
                setSupplierIdChecked(true);
                setSupplierInfo(response.supplierInfo);
                setSupplierError('');
            } else {
                setSupplierError('등록되지 않은 협력사 ID입니다. 배민 어드민에 등록된 협력사 ID를 입력해주세요.');
                setSupplierIdChecked(false);
                setSupplierInfo(null);
            }
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err, '협력사 ID 확인에 실패했습니다.');
            setSupplierError(errorMessage);
            setSupplierIdChecked(false);
            setSupplierInfo(null);
        }
    }, [supplierId, checkSupplierId]);

    /**
     * 본사 코드 입력 변경 핸들러
     */
    const handleHeadquartersCodeChange = useCallback((value: string) => {
        setHeadquartersCode(value);
        setHeadquartersCodeChecked(false);
        setHeadquartersError('');
    }, []);

    /**
     * 협력사 ID 입력 변경 핸들러
     */
    const handleSupplierIdChange = useCallback((value: string) => {
        setSupplierId(value);
        setSupplierIdChecked(false);
        setSupplierInfo(null);
        setSupplierError('');
    }, []);

    /**
     * 본사 회원가입 페이지 이동
     */
    const handleHeadquartersRegister = useCallback(() => {
        if (headquartersCodeChecked) {
            navigate('/register/headquarters');
        }
    }, [headquartersCodeChecked, navigate]);

    /**
     * 협력사 회원가입 페이지 이동
     */
    const handleSupplierRegister = useCallback(() => {
        if (supplierIdChecked && supplierInfo) {
            navigate('/register/supplier', { state: { supplierId, supplierInfo } });
        }
    }, [supplierIdChecked, supplierInfo, supplierId, navigate]);

    /**
     * 로그인 페이지 이동
     */
    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    /**
     * 버튼 호버 이벤트 핸들러
     */
    const handleButtonMouseEnter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.color = '#05CD82';
    }, []);

    const handleButtonMouseLeave = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.color = '#666666';
    }, []);

    // ======== Render ========
    return (
        <div className="login-page">
            <div className="login-container">
                {/* 좌측 브랜드 섹션 */}
                <BrandSection
                    title="라이더 정산 시스템"
                    description={brandDescription}
                    features={LOGIN_FEATURES}
                />

                {/* 우측 회원가입 유형 선택 섹션 */}
                <div className="login-section">
                    <div className="login-form-container">
                        {/* 헤더 */}
                        <div className="login-header">
                            <h2>회원가입</h2>
                            <p>가입하실 계정 유형을 선택해주세요</p>
                        </div>

                        {/* 회원가입 카드 목록 */}
                        <div style={CARD_CONTAINER_STYLE}>
                            {/* 본사 회원가입 카드 */}
                            <RegisterCard
                                title="본사 관리자"
                                description="VROONG 본사 관리자 계정을 생성합니다. 전체 협력사 관리 및 통계를 확인할 수 있습니다."
                                icon="본"
                                gradientColors={HEADQUARTERS_COLORS.gradient}
                                borderColor={HEADQUARTERS_COLORS.border}
                                backgroundColor={HEADQUARTERS_COLORS.background}
                                isChecked={headquartersCodeChecked}
                                onClick={handleHeadquartersRegister}
                                inputLabel="본사 직원 확인 코드"
                                inputPlaceholder="직원 확인 코드를 입력하세요"
                                inputValue={headquartersCode}
                                onInputChange={handleHeadquartersCodeChange}
                                isLoading={isCheckingCode}
                                onCheckClick={handleCheckHeadquartersCode}
                                checkButtonColor={HEADQUARTERS_COLORS.button}
                                error={headquartersError}
                                inputType="password"
                                successMessage={headquartersSuccessMessage}
                            />

                            {/* 협력사 회원가입 카드 */}
                            <RegisterCard
                                title="협력사"
                                description="협력사 계정을 생성합니다. 정산 정보 조회 및 라이더 관리를 할 수 있습니다."
                                icon="협"
                                gradientColors={SUPPLIER_COLORS.gradient}
                                borderColor={SUPPLIER_COLORS.border}
                                backgroundColor={SUPPLIER_COLORS.background}
                                isChecked={supplierIdChecked}
                                onClick={handleSupplierRegister}
                                inputLabel="배민 어드민 협력사 ID"
                                inputPlaceholder="협력사 ID를 입력하세요"
                                inputValue={supplierId}
                                onInputChange={handleSupplierIdChange}
                                isLoading={isLoading}
                                onCheckClick={handleCheckSupplierId}
                                checkButtonColor={SUPPLIER_COLORS.button}
                                error={supplierError}
                                successMessage={supplierSuccessMessage}
                            />
                        </div>

                        {/* 추가 옵션 */}
                        <div className="additional-options" style={ADDITIONAL_OPTIONS_STYLE}>
                            <div className="divider">
                                <span>또는</span>
                            </div>

                            <div className="help-links">
                                <button
                                    type="button"
                                    onClick={handleNavigateToLogin}
                                    style={LOGIN_LINK_STYLE}
                                    onMouseEnter={handleButtonMouseEnter}
                                    onMouseLeave={handleButtonMouseLeave}
                                >
                                    <LoginIcon width={16} height={16} />
                                    이미 계정이 있으신가요? 로그인
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 하단 버전 정보 */}
                    <VersionInfo />
                </div>
            </div>
        </div>
    );
};

export default RegisterSelectPage;
